import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Image,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getActiveIssueDescription,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
} from '../../../../../utils/fn/medias';
import MediaTagsInline from './MediaTagsInline';
import MediaThumbnail from './MediaThumbnail';

const { Text } = Typography;

/** Largeur (px) en dessous de laquelle on n’affiche qu’un seul tag. */
const TAG_COUNT_BREAKPOINT = 200;

/** Doit rester aligné sur `styles.body.padding` de la Card (même valeur horizontale). */
const getBodyHorizontalPaddingPx = (isLargeSize) => (isLargeSize ? 12 : 8);

const iconOnlyTagBaseStyle = {
  margin: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  lineHeight: 1,
  cursor: 'pointer',
};

const MediaCard = ({
  media,
  size = 'large',
  onClick,
  isSelected = false,
  onToggle,
  onValidateMedia,
  setHasRejectedMedias,
}) => {
  const isLarge = size === 'large';
  const thumbnailSize = isLarge ? 'large' : 'medium';
  const [isHovered, setIsHovered] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [maxVisibleTags, setMaxVisibleTags] = useState(2);
  const cardWidthRef = useRef(null);

  const isImage = media.type === 'image';
  const isPdf =
    media.type === 'document' &&
    (media.format === 'pdf' ||
      media.name?.toLowerCase().endsWith('.pdf') ||
      media.cdn_link?.toLowerCase().endsWith('.pdf') ||
      media.local_file?.toLowerCase().endsWith('.pdf'));
  const isPreviewable = isImage || isPdf;
  const tags = media.tags ?? [];
  const issueDescription = getActiveIssueDescription(media);
  const isToValidate = media.status === 'to_validate';
  const showValidationActions =
    isToValidate && !media.is_archived && isHovered && onValidateMedia;

  useEffect(() => {
    const el = cardWidthRef.current;
    if (!el) return undefined;
    const horizontalPad = getBodyHorizontalPaddingPx(isLarge);

    const applyWidth = (width) => {
      if (width <= 0) return;
      // Boîte de contenu du wrapper (déjà hors padding du wrapper). Largeur utile
      // pour la ligne de tags = moins le padding horizontal du body Card.
      const usableTagRowWidth = Math.max(0, width - 2 * horizontalPad);
      setMaxVisibleTags(
        usableTagRowWidth > 0 && usableTagRowWidth < TAG_COUNT_BREAKPOINT
          ? 1
          : 2
      );
    };

    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      applyWidth(w);
    });
    observer.observe(el);
    applyWidth(el.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, [isLarge]);

  const handleCardClick = useCallback(() => {
    if (onToggle) {
      onToggle(media.id);
    } else if (onClick) {
      onClick();
    }
  }, [onToggle, onClick, media.id]);

  const handleCheckboxClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onToggle) {
        onToggle(media.id);
      }
    },
    [onToggle, media.id]
  );

  const handlePreviewClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setPreviewVisible(true);
  }, []);

  const handleIssueBadgeClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleStatusTagClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleValidateMediaClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onValidateMedia?.(media.id);
    },
    [onValidateMedia, media.id]
  );

  const handleRefuseMediaClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      console.log('handleRefuseMediaClick', media.id);
      onToggle?.(media.id);
      setHasRejectedMedias(true);
    },
    [media.id]
  );

  const cardStyle = useMemo(
    () => ({
      border: isSelected ? '1px solid #22247B' : undefined,
      boxShadow: isSelected ? '0 2px 5px 1px #22247B' : undefined,
      borderRadius: isSelected ? '8px' : undefined,
      overflow: 'hidden',
      padding: isSelected ? '1px' : undefined,
    }),
    [isSelected]
  );

  const tagRow = tags.length > 0 && (
    <MediaTagsInline
      tags={tags}
      maxVisible={maxVisibleTags}
      size={isLarge ? 'large' : 'small'}
    />
  );

  const cardInner = (
    <>
      <Card
        hoverable
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        cover={
          <div style={{ position: 'relative' }}>
            <MediaThumbnail media={media} size={thumbnailSize} />
            <div
              style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                zIndex: 10,
              }}
            >
              <Checkbox checked={isSelected} onClick={handleCheckboxClick} />
            </div>
            {showValidationActions && (
              <div
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 30,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  maxWidth: 'calc(100% - 16px)',
                }}
              >
                {onValidateMedia && (
                  <Tooltip title="Valider le média">
                    <Tag
                      color="success"
                      variant="solid"
                      onClick={handleValidateMediaClick}
                      style={{
                        ...iconOnlyTagBaseStyle,
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        color: '#fff',
                      }}
                      icon={
                        <CheckOutlined
                          style={{
                            fontSize: isLarge ? 16 : 14,
                            color: '#fff',
                          }}
                        />
                      }
                    />
                  </Tooltip>
                )}

                <Tooltip title="Refuser le média">
                  <Tag
                    color="error"
                    variant="solid"
                    onClick={handleRefuseMediaClick}
                    style={{
                      ...iconOnlyTagBaseStyle,
                      backgroundColor: '#ff4d4f',
                      borderColor: '#ff4d4f',
                      color: '#fff',
                    }}
                    icon={
                      <CloseOutlined
                        style={{
                          fontSize: isLarge ? 16 : 14,
                          color: '#fff',
                        }}
                      />
                    }
                  />
                </Tooltip>
              </div>
            )}
            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                left: '8px',
                zIndex: 20,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                maxWidth: 'calc(100% - 16px)',
              }}
            >
              {issueDescription && (
                <Tooltip
                  title={issueDescription}
                  color="#ff4d4f"
                  styles={{ body: { color: '#fff' } }}
                >
                  <span
                    onClick={handleIssueBadgeClick}
                    style={{ display: 'inline-block', lineHeight: 0 }}
                  >
                    <Tag
                      color="error"
                      variant="solid"
                      style={{
                        margin: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px',
                        lineHeight: 1,
                        cursor: 'help',
                        backgroundColor: '#ff4d4f',
                        borderColor: '#ff4d4f',
                        color: '#fff',
                      }}
                      icon={
                        <ExclamationCircleOutlined
                          style={{
                            fontSize: isLarge ? 16 : 14,
                            color: '#fff',
                          }}
                        />
                      }
                    />
                  </span>
                </Tooltip>
              )}
              <span
                onClick={handleStatusTagClick}
                style={{ display: 'inline-block', lineHeight: 0 }}
              >
                <Tag
                  icon={getStatusIcon(media.status)}
                  color={media.statusColor ?? getStatusColor(media.status)}
                  style={{ margin: 0 }}
                >
                  {getStatusLabel(media.status)}
                </Tag>
              </span>
            </div>
            {isPreviewable && isHovered && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                }}
              >
                <Button
                  type="primary"
                  shape="circle"
                  size="large"
                  icon={<EyeOutlined />}
                  onClick={handlePreviewClick}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  }}
                />
              </div>
            )}
          </div>
        }
        style={cardStyle}
        styles={{
          body: { padding: isLarge ? '12px' : '8px' },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minWidth: 0,
          }}
        >
          <Text
            strong={isLarge}
            ellipsis={{ tooltip: `${media.name} (${media.product_reference})` }}
            style={{ fontSize: isLarge ? '14px' : '12px' }}
          >
            {media.name} ({media.product_reference})
          </Text>
          {tagRow}
        </div>
      </Card>
      {isPreviewable && (
        <Image
          src={media.cdn_link ?? media.local_file}
          alt={media.name}
          style={{ display: 'none' }}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            mask: false,
            imageRender: isPdf
              ? () => (
                  <iframe
                    src={media.cdn_link || media.local_file}
                    style={{
                      width: '100%',
                      height: '80vh',
                      border: 'none',
                    }}
                    title={media.name}
                  />
                )
              : undefined,
            toolbarRender: isPdf
              ? () => <div style={{ display: 'none' }} />
              : undefined,
          }}
        />
      )}
    </>
  );

  return (
    <div ref={cardWidthRef} style={{ width: '100%' }}>
      {media.is_new ? (
        <Badge.Ribbon text="Nouveau" color="green">
          <div>{cardInner}</div>
        </Badge.Ribbon>
      ) : (
        cardInner
      )}
    </div>
  );
};

export default React.memo(MediaCard);
